<template>
  <div ref="customControlsWrapper">
    <div ref="customControls" class="mt-3" v-bind:style="customControlsStyle">
      <VDefaultsProvider :defaults="{ VMenu: { attach: fullscreen }, VTooltip: { attach: fullscreen } }">

      <!-- controls on row 1 -->
      <div class="d-flex flex-row" data-cy="playerControls-firstRow">

        <div
          ref="previewWindow"
          class="previewWindow"
          v-bind:style="{ backgroundImage: 'url(' + livePreviewImage + ')' }">
        </div>

        <v-slider
          ref='sliderComponent'
          v-model="timeSliderValue"
          @update:model-value="seekSlider"
          :disabled="castSeekDisabled"
          :aria-disabled="castSeekDisabled"
          :aria-describedby="castDescriptionId"
          @start="startTimeSeeking"
          @end="endTimeSeeking"
          hide-details="true"
          :show-ticks="false"
          thumb-size="20"
          thumb-color="primary"
          track-fill-color="primary"
          track-color="sliderTrackColor"
          track-size="8"
          step="0"
          min="0"
          :max="duration * 100"
          :aria-label="$t('routes.pod.timeline')"
          aria-orientation="horizontal"
          aria-valuemin="0"
          :aria-valuemax="duration * 100"
          :aria-valuetext="currentTimeAriaText"
          :aria-valuenow="timeSliderValue"
          role="slider"
          density="comfortable"
        />
      </div>

      <!-- Comments dots -->
      <CommentDots v-if="comments && comments.length > 0" :comments="comments" />

      <!-- controls on row 2 -->
      <div
        class="mt-2 d-flex justify-space-between player-controls-second-row"
        data-cy="playerControls-secondRow"
      >
        <!-- Left side buttons -->
        <div class="d-flex" style="">
          <v-tooltip location="bottom" open-delay="200" :aria-label="$t('routes.pod.minus1second')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="seekSeconds(-1)"
                :disabled="castSeekDisabled"
                :aria-describedby="castDescriptionId"
                data-cy="playerControls-rewind1s-btn"
                :aria-label="$t('routes.pod.minus1second')"
              >
                <v-icon>mdi-rewind</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.minus1second') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" open-delay="200" :aria-label="$t('routes.pod.minus10frames')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="plyrSeekFrames(-10)"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                data-cy="playerControls-rewind10f-btn"
                :aria-label="$t('routes.pod.minus10frames')"
              >
                <v-icon>mdi-rewind-10</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.minus10frames') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" open-delay="200" :aria-label="$t('routes.pod.playPause')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="playToggle()"
                :disabled="castPauseDisabled"
                :aria-describedby="castDescriptionId"
                color="primary"
                data-cy="playerControls-play-btn"
                :aria-pressed="isPlaying"
                :aria-label="$t('routes.pod.playPause')"
              >
                <v-icon size="25">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.playPause') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" open-delay="200" :aria-label="$t('routes.pod.plus10frames')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="plyrSeekFrames(10)"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                data-cy="playerControls-forward10f-btn"
                :aria-label="$t('routes.pod.plus10frames')"
              >
                <v-icon>mdi-fast-forward-10</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.plus10frames') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" open-delay="200" :aria-label="$t('routes.pod.plus1second')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="seekSeconds(1)"
                :disabled="castSeekDisabled"
                :aria-describedby="castDescriptionId"
                data-cy="playerControls-forward1s-btn"
                :aria-label="$t('routes.pod.plus1second')"
              >
                <v-icon size="20px" tile>mdi-fast-forward</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.plus1second') }}</span>
          </v-tooltip>
        </div>

        <!-- Center content -->
        <div class="d-flex align-baseline justify-center mt-1 playback-time" style="">
          <v-menu eager location="top left">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                class="player-controls-time-format-btn"
                data-cy="playerControls-timeFormat"
                :aria-label="timeFormatButtonLabel"
              >
                <small v-if="!playerTimeFormat">{{ toTimeFrames(currentTime, transcodedAsset.framerate) }}</small>
                <small v-else>{{ toTimeMilliseconds(currentTime) }}</small>
              </v-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="timeFormatButtonLabel">
              <v-list-item
                :active="!playerTimeFormat"
                v-on:click="setTimeFormat(false)"
              >
                <v-list-item-title>{{ $t('routes.pod.formatAsFrames') }}</v-list-item-title>
                <template v-slot:append v-if="!playerTimeFormat">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="playerTimeFormat"
                v-on:click="setTimeFormat(true)"
              >
                <v-list-item-title>{{ $t('routes.pod.formatAsMilliseconds') }}</v-list-item-title>
                <template v-slot:append v-if="playerTimeFormat">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <div>
            <small>-&nbsp;&nbsp;{{ videoDuration(duration) }}</small>
          </div>
        </div>

        <!-- Spacer -->
        <!-- <div class="text-center" style="width: 15%;"></div> -->

        <!-- Right side buttons -->
        <div class="d-flex flex-row" style="">
          <v-tooltip v-if="enabledWaveform" location="bottom" open-delay="200" :aria-label="$t('routes.pod.waveform')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="waveformToggleClick()"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-bind:color="waveformToggle ? 'primary' : ''"
                :aria-pressed="waveformToggle"
                :aria-label="$t('routes.pod.waveform')"
              >
                <v-icon>mdi-waveform</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.waveform') }}</span>
          </v-tooltip>

          <v-menu
            v-if="getAvailableLayers.length"
            v-model="layersMenuOpen"
            :disabled="castLocalFeaturesLocked"
            :location="$vuetify.display.smAndUp ? 'top' : undefined"
            eager
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                :disabled="castLocalFeaturesLocked"
                :aria-disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                width="32px"
                :aria-label="$t('routes.pod.playerLayers')"
              >
                <v-icon>mdi-layers-triple-outline</v-icon>
              </v-small-icon-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="$t('routes.pod.playerLayers')">
              <v-list-item
                v-for="layer in getAvailableLayers"
                :key="layer.id"
                :active="getActiveLayer === layer.id"
                role="menuitemradio"
                :aria-checked="getActiveLayer === layer.id"
                :disabled="castLocalFeaturesLocked"
                :aria-disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                @click="updateActiveLayer(layer.id)"
              >
                <v-list-item-title>{{ layer.text }}</v-list-item-title>
                <template v-slot:append v-if="getActiveLayer === layer.id">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>


          <v-menu location="top center" :close-on-content-click="false" eager>
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                :aria-label="$t('routes.pod.playbackSpeed')"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
              >
                <small v-if="speed === 2">2.0x</small>
                <small v-if="speed === 1.5">1.5x</small>
                <small v-if="speed === 1.25">1.25x</small>
                <v-icon v-if="speed === 1">mdi-play-speed</v-icon>
                <small v-if="speed === 0.75">0.75x</small>
                <small v-if="speed === 0.5">0.5x</small>
              </v-small-icon-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="$t('routes.pod.playbackSpeed')">
              <v-list-item
                :active="speed === 2"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(2.0)"
              >
                <v-list-item-title>2.0x</v-list-item-title>
                <template v-slot:append v-if="speed === 2">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="speed === 1.5"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(1.5)"
              >
                <v-list-item-title>1.5x</v-list-item-title>
                <template v-slot:append v-if="speed === 1.5">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="speed === 1.25"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(1.25)"
              >
                <v-list-item-title>1.25x</v-list-item-title>
                <template v-slot:append v-if="speed === 1.25">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="speed === 1"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(1.0)"
              >
                <v-list-item-title>1.0x</v-list-item-title>
                <template v-slot:append v-if="speed === 1">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="speed === 0.75"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(0.75)"
              >
                <v-list-item-title>0.75x</v-list-item-title>
                <template v-slot:append v-if="speed === 0.75">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                :active="speed === 0.5"
                :disabled="castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                v-on:click="changePlayingSpeed(0.5)"
              >
                <v-list-item-title>0.5x</v-list-item-title>
                <template v-slot:append v-if="speed === 0.5">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu
            v-if="showQualitySelection"
            :location="$vuetify.display.smAndUp ? 'top center' : undefined"
            eager
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                :disabled="airPlayActive || castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                :aria-label="$t('routes.pod.videoQuality')"
              >
                <v-icon>mdi-quality-high</v-icon>
              </v-small-icon-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="$t('routes.pod.videoQuality')">
              <v-list-item
                v-for="quality in qualityOptions"
                :key="quality.value"
                :active="activeQuality === quality.value"
                role="menuitemradio"
                :aria-checked="activeQuality === quality.value"
                :disabled="airPlayActive || castLocalFeaturesLocked"
                :aria-disabled="airPlayActive || castLocalFeaturesLocked"
                :aria-describedby="castDescriptionId"
                @click="setQuality(quality.value)"
              >
                <v-list-item-title>{{ quality.text }}</v-list-item-title>
                <template v-slot:append v-if="activeQuality === quality.value">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu
            v-if="availableSubtitles.length"
            :location="$vuetify.display.smAndUp ? 'top center' : undefined"
            eager
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                :aria-label="$t('menus.topBars.subtitles')"
                :disabled="!!castUi?.busy"
                :aria-describedby="castDescriptionId"
              >
                <v-icon>mdi-closed-caption-outline</v-icon>
              </v-small-icon-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="$t('menus.topBars.subtitles')">
              <v-list-item
                v-for="sub in availableSubtitles"
                :key="sub.id || sub.transcriptId || 'off'"
                :active="isActiveSubtitle(sub)"
                role="menuitemradio"
                :aria-checked="isActiveSubtitle(sub)"
                :disabled="subtitleDisabled(sub)"
                :aria-disabled="subtitleDisabled(sub)"
                :aria-describedby="castLocalFeaturesLocked ? castDescriptionId : (airPlayActive && sub.url ? airPlayDescriptionId : undefined)"
                v-on:click="setActiveSubtitle(sub)"
              >
                <v-list-item-title>{{ sub.label === 'Off' ? $t('common.off') : sub.label }}</v-list-item-title>
                <template v-slot:append v-if="isActiveSubtitle(sub)">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu
            v-if="availableAudioDescriptions.length"
            :location="$vuetify.display.smAndUp ? 'top center' : undefined"
            eager
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                :aria-label="$t('routes.pod.audioDescriptions')"
                :disabled="!!castUi?.busy"
                :aria-describedby="castDescriptionId"
              >
                <v-icon>mdi-account-voice</v-icon>
              </v-small-icon-btn>
            </template>

            <v-list density="compact" role="menu" :aria-label="$t('routes.pod.audioDescriptions')">
              <v-list-item
                v-for="audioDescription in availableAudioDescriptions"
                v-bind:key="audioDescription.id || 'off'"
                :active="isActiveAudioDescription(audioDescription)"
                role="menuitemradio"
                :aria-checked="isActiveAudioDescription(audioDescription)"
                :disabled="audioDescriptionDisabled(audioDescription)"
                :aria-disabled="audioDescriptionDisabled(audioDescription)"
                :aria-describedby="castLocalFeaturesLocked ? castDescriptionId : (airPlayActive && audioDescription.url ? airPlayDescriptionId : undefined)"
                v-on:click="setActiveAudioDescription(audioDescription)"
              >
                <v-list-item-title>{{ audioDescription.label === 'Off' ? $t('common.off') : audioDescription.label }}</v-list-item-title>
                <template v-slot:append v-if="isActiveAudioDescription(audioDescription)">
                  <v-icon size="20px">mdi-check-bold</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu location="top center" :close-on-content-click="false" eager>
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="volumeToggle()"
                :disabled="castVolumeDisabled"
                :aria-describedby="castDescriptionId"
                :aria-pressed="muted"
                :aria-label="muted ? $t('routes.pod.volume') : $t('routes.pod.mute')"
              >
                <v-icon>{{ volumeIcon }}</v-icon>
              </v-small-icon-btn>
            </template>

            <v-card height="200" width="70" elevation="4" class="pt-4">
              <v-slider
                :model-value="volume * 100"
                :disabled="castVolumeDisabled"
                :aria-describedby="castDescriptionId"
                v-on:update:model-value="changeVolume"
                color="primary"
                hide-details="true"
                track-color="sliderTrackColor"
                min="0"
                max="100"
                density="compact"
                direction="vertical"
                :aria-label="$t('routes.pod.volume')"
              />
            </v-card>
          </v-menu>

          <v-tooltip
            v-if="airPlayVisible"
            location="bottom"
            open-delay="200"
            :aria-label="$t('routes.pod.airPlay.button')"
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                @click="$emit('airplay')"
                data-cy="playerControls-airPlay-btn"
                :disabled="airPlayDisabled || castLocalFeaturesLocked"
                :color="airPlayActive ? 'primary' : ''"
                :aria-pressed="airPlayActive"
                :aria-label="$t('routes.pod.airPlay.button')"
                :aria-describedby="airPlayActive || (airPlayDisabled && airPlayDisabledReason) ? airPlayDescriptionId : undefined"
              >
                <v-icon>mdi-cast-variant</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.airPlay.button') }}</span>
          </v-tooltip>

          <v-menu
            v-if="castUi?.connected"
            v-model="castSessionMenuOpen"
            location="top center"
            :disabled="!!castUi.busy"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                data-cy="playerControls-cast-btn"
                :disabled="!!castUi.busy"
                :color="castUi.active ? 'primary' : ''"
                :aria-pressed="!!castUi.active"
                :aria-label="$t('routes.pod.googleCast.controls')"
                :aria-describedby="castDescriptionId"
              >
                <v-icon>mdi-cast</v-icon>
              </v-small-icon-btn>
            </template>
            <v-list density="compact" role="menu" :aria-label="$t('routes.pod.googleCast.controls')">
              <v-list-item
                v-if="!castUi.active"
                role="menuitem"
                data-cy="playerControls-castThisVideo-btn"
                :disabled="castUi.disabled || castUi.busy || airPlayActive"
                :aria-describedby="castDescriptionId"
                @click="startCast"
              >
                <v-list-item-title>{{ $t('routes.pod.googleCast.castThisVideo') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                role="menuitem"
                data-cy="playerControls-stopCast-btn"
                :disabled="!!castUi.busy"
                :aria-describedby="castDescriptionId"
                @click="stopCast"
              >
                <v-list-item-title>{{ $t(castUi.active ? 'routes.pod.googleCast.stop' : 'routes.pod.googleCast.stopSession') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-tooltip v-else-if="castUi?.visible" location="bottom" open-delay="200" :aria-label="$t('routes.pod.googleCast.button')">
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                @click="startCast"
                data-cy="playerControls-cast-btn"
                :disabled="castUi.disabled || castUi.busy || castUi.active || airPlayActive"
                :color="castUi.active ? 'primary' : ''"
                :aria-pressed="!!castUi.active"
                :aria-label="$t('routes.pod.googleCast.button')"
                :aria-describedby="castDescriptionId"
              >
                <v-icon>mdi-cast</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.googleCast.button') }}</span>
          </v-tooltip>
          <v-btn
            v-if="castUi?.resumeLocal"
            @click="resumeLocal"
            data-cy="playerControls-resumeLocal-btn"
            :disabled="!!castUi.busy"
            :aria-describedby="castDescriptionId"
          >{{ $t('routes.pod.googleCast.resumeLocal') }}</v-btn>

          <v-tooltip
            v-if="showFullscreen"
            location="bottom"
            open-delay="200"
            :aria-label="$t('routes.pod.fullScreen')"
          >
            <template v-slot:activator="{ props }">
              <v-small-icon-btn
                v-bind="props"
                v-on:click="fullscreenToggle()"
                data-cy="playerControls-fullScreen-btn"
                :aria-pressed="fullscreen"
                :aria-label="$t('routes.pod.fullScreen')"
              >
                <v-icon>mdi-fullscreen</v-icon>
              </v-small-icon-btn>
            </template>
            <span>{{ $t('routes.pod.fullScreen') }}</span>
          </v-tooltip>
        </div>
      </div>
      </VDefaultsProvider>
      <div
        v-if="airPlayDescriptionId"
        :id="airPlayDescriptionId"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        class="text-body-2"
      >
        <p v-if="airPlayActive" class="mt-2 mb-0">
          {{ $t('routes.pod.airPlay.active') }}
          {{ $t('routes.pod.airPlay.returnLocal') }}
          {{ $t('routes.pod.airPlay.returnLocalForAccessibility') }}
        </p>
        <p v-else-if="airPlayVisible && airPlayDisabled && airPlayDisabledReason" class="mt-2 mb-0">
          {{ airPlayDisabledReason }}
        </p>
      </div>
      <div v-if="airPlayDescriptionId && airPlayErrorKey" role="alert" aria-atomic="true" class="text-body-2 text-error">
        <p class="mt-2 mb-0">{{ $t(airPlayErrorKey) }}</p>
      </div>
      <div
        v-if="castUi"
        :id="castDescriptionId"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        class="text-body-2"
      >
        <p v-if="castUi.active" class="mt-2 mb-0">{{ $t('routes.pod.googleCast.active') }}</p>
        <p v-else-if="castUi.connected" class="mt-2 mb-0">
          {{ $t('routes.pod.googleCast.connected') }} {{ $t('routes.pod.googleCast.otherMedia') }}
        </p>
        <p v-if="castLocalFeaturesLocked" class="mt-2 mb-0">
          {{ $t('routes.pod.googleCast.localFeaturesUnavailable') }}
          {{ $t('routes.pod.googleCast.returnLocalForAccessibility') }}
          {{ $t('routes.pod.googleCast.layersUnavailable') }}
        </p>
        <p v-if="castUi.active && !castUi.canPause" class="mt-2 mb-0">{{ $t('routes.pod.googleCast.pauseUnavailable') }}</p>
        <p v-if="castUi.active && !castUi.canSeek" class="mt-2 mb-0">{{ $t('routes.pod.googleCast.seekUnavailable') }}</p>
        <p v-if="castUi.active && !castUi.canControlVolume" class="mt-2 mb-0">{{ $t('routes.pod.googleCast.volumeUnavailable') }}</p>
        <p v-if="castUi.visible && castUi.disabled && castUi.disabledReasonKey" class="mt-2 mb-0">{{ $t(castUi.disabledReasonKey) }}</p>
        <p v-if="castUi.errorKey" class="mt-2 mb-0 text-error">{{ $t(castUi.errorKey) }}</p>
        <p v-if="castUi.resumeLocal" class="mt-2 mb-0">{{ $t('routes.pod.googleCast.resumeLocal') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import CommentDots from './CommentDots.vue'
import { toTimeFrames, toTimeMilliseconds, videoDuration } from '@/components/formatters.js';
import { promisify } from '@/components/promisify.js';

export default {
  emits: ["waveformToggle", "qualityChange", "airplay", "cast", "stop-cast", "resume-local", "cast-caption-change"],
  name: "PlayerControls",
  components: {
    CommentDots,
  },
  props: {
    playbackCommands: {
      type: Object,
      default: null,
    },
    castUi: {
      type: Object,
      default: null,
    },
    transcodedAsset: {
      type: Object,
      required: true,
    },
    pod: {
      type: Object,
      required: false,
    },
    enabledWaveform: {
      type: Boolean,
      required: false,
      default: false,
    },
    showQualitySelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    qualityOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    activeQuality: {
      type: String,
      required: false,
      default: 'auto',
    },
    showFullscreen: {
      type: Boolean,
      required: false,
      default: true,
    },
    airPlayVisible: {
      type: Boolean,
      default: false,
    },
    airPlayActive: {
      type: Boolean,
      default: false,
    },
    airPlayDisabled: {
      type: Boolean,
      default: false,
    },
    airPlayDisabledReason: {
      type: String,
      default: null,
    },
    airPlayDescriptionId: {
      type: String,
      default: null,
    },
    airPlayErrorKey: {
      type: String,
      default: null,
    },
    comments: {
      type: Array,
      required: false,
    }
  },
  data: function() {
    // Using a random string if we have more than one player in the page
    let randomString = (Math.random() + 1).toString(36).substring(7);
    return {
      playerTimeFormat: true,
      layersMenuOpen: false,
      castSessionMenuOpen: false,
      defaultCastDescriptionId: 'google-cast-status-' + randomString,
      selectorMenuTimes: 'selector-menu-time-' + randomString,
      selectorMenuSubtitles: 'selector-menu-subtitles-' + randomString,
      selectorMenuSpeed: 'selector-menu-speed-'+randomString,
      selectorMenuLayers: 'selector-menu-layers-'+randomString,
      selectorMenuVolume: 'selector-menu-volume-'+randomString,
      timeSliderValue: 0,
      draggingScrubSlider: false,
      plyrShouldPlay: false,
      livePreviewImage: '',
      waveformToggle: false,
      playPausePromise: null,
    };
  },

  mounted: function() {
    this.refreshLivepreviewImage();

    if (this.livePreviewImage) {
      const sliderLine = this.$refs.sliderComponent.$el.getElementsByClassName('v-slider-track')[0];
      sliderLine.onmouseenter = sliderLine.onmousemove = this.showPreviewWindow.bind(this);
      sliderLine.onmouseout = this.hidePreviewWindow.bind(this);
    }
  },
  methods: {
    videoDuration,
    toTimeFrames,
    toTimeMilliseconds,
    playToggle: function() {
      if (this.castPauseDisabled) return;
      this.handlePlayPauseCall(this.playerTogglePlay);
    },
    play: function() {
      if (this.castPauseDisabled) return;
      this.handlePlayPauseCall(this.playerPlay);
    },
    pause: function() {
      if (this.castPauseDisabled) return;
      this.handlePlayPauseCall(this.playerPause);
    },
    handlePlayPauseCall: function(fn) {
      if (this.playPausePromise) {
        this.playPausePromise.then(fn);
      } else {
        this.playPausePromise = fn();
      }

      this.playPausePromise.finally(() => {
        this.playPausePromise = null;
      });
    },
    startTimeSeeking: function() {
      if (this.castSeekDisabled) return;
      // While seeking the video these events are triggered in the following order:
      // 1. MouseDown event
      // 2. Input event
      // 3. MouseUp event
      // 4. Changed event
      this.plyrShouldPlay = this.isPlaying;
      if (!this.castPauseDisabled) this.pause();
      this.draggingScrubSlider = true;
      this.hidePreviewWindow();
    },
    endTimeSeeking: function() {
      this.draggingScrubSlider = false;

      if (this.plyrShouldPlay && !this.castPauseDisabled) {
        this.play();
      }
    },
    seekPlayback: function(seconds) {
      if (this.castSeekDisabled) return;
      if (this.playbackCommands) {
        this.playbackCommands.seek(seconds);
      } else {
        this.plyr.currentTime = seconds;
      }
    },
    seekSlider: function(value) {
      if (!this.draggingScrubSlider) this.seekPlayback(value / 100);
    },
    seekSeconds: function(seconds) {
      const position = this.castUi?.active ? this.currentTime : this.plyr.currentTime;
      const target = position + seconds;
      this.seekPlayback(this.castUi?.active ? Math.max(0, Math.min(this.duration, target)) : target);
    },
    plyrSeekFrames: function(frames) {
      if (this.castLocalFeaturesLocked) return;
      const framerate = this.transcodedAsset.framerate || 24;
      this.seekSeconds(frames / framerate);
    },
    startCast: function() {
      if (!this.castUi || this.castUi.disabled || this.castUi.busy || this.castUi.active || this.airPlayActive) return;
      this.$emit('cast');
    },
    stopCast: function() {
      if (!this.castUi?.connected || this.castUi.busy) return;
      this.$emit('stop-cast');
    },
    resumeLocal: function() {
      if (!this.castUi?.resumeLocal || this.castUi.busy) return;
      this.$emit('resume-local');
    },
    subtitleDisabled: function(sub) {
      return !!this.castUi?.busy ||
        (this.airPlayActive && !!sub?.url) ||
        (!!this.castUi?.active && !!sub?.url && sub.id !== (this.castUi.loadedCaptionAssetId ?? this.castUi.captionAssetId));
    },
    audioDescriptionDisabled: function(audioDescription) {
      return !!this.castUi?.busy ||
        ((this.airPlayActive || !!this.castUi?.active) && !!audioDescription?.url);
    },
    setTimeFormat: function(asMilliseconds) {
      this.playerTimeFormat = asMilliseconds;
    },
    volumeToggle: function() {
      if (this.castVolumeDisabled) return;
      this.$parent.togglePlayerMuted();
    },
    changeVolume: function(v) {
      if (this.castVolumeDisabled) return;
      this.$parent.setPlayerVolume(v / 100);
    },
    fullscreenToggle: function() {
      this.plyr.fullscreen.toggle();
    },
    changePlayingSpeed: function(speed) {
      if (this.castLocalFeaturesLocked) return;
      this.plyr.speed = speed;
    },
    setQuality: function (quality) {
      if (this.airPlayActive || this.castLocalFeaturesLocked) {
        return;
      }
      this.$emit("qualityChange", quality);
    },
    setActiveSubtitle: function(sub) {
      if (this.subtitleDisabled(sub)) return;
      if (this.castUi?.active) {
        this.$emit('cast-caption-change', sub?.url ? sub : null);
        return;
      }
      const playerStore = usePlayerStore();
      playerStore.setActiveSubtitle(sub);
    },
    setActiveAudioDescription: function(audioDescription) {
      if (this.audioDescriptionDisabled(audioDescription)) return;
      const playerStore = usePlayerStore();
      playerStore.setActiveAudioDescription(audioDescription);
    },
    refreshLivepreviewImage: function() {
      const hasLivepreviewImage =
        this.pod &&
        this.pod.asset &&
        this.pod.asset.livepreview &&
        this.pod.asset.livepreview[0] &&
        this.pod.asset.livepreview[0].state === this.assetStates.COMPLETE &&
        this.pod.asset.livepreview[0].url;

      if (hasLivepreviewImage) {
        this.livePreviewImage = this.pod.asset.livepreview[0].url;
      }
    },
    showPreviewWindow: function(e) {
      if(this.draggingScrubSlider) {
        return;
      }

      const totalFrames = 140;
      const boundingBox = e.currentTarget.getBoundingClientRect();
      const frameWidth = boundingBox.width / totalFrames;

      let currentFrame = Math.floor((e.clientX - boundingBox.x) / frameWidth);
      if (currentFrame >= totalFrames) {
        currentFrame = totalFrames - 1;
      } else if (currentFrame < 0) {
        currentFrame = 0;
      }

      this.$refs.previewWindow.style["background-position-x"] =
        -currentFrame * 140 + "px";
      this.$refs.previewWindow.style.left = (e.x - 72) + 'px';
      this.$refs.previewWindow.style.top = (boundingBox.top - 84) + 'px';
      this.$refs.previewWindow.style.display = "block";
    },
    hidePreviewWindow: function() {
      this.$refs.previewWindow.style.display = "none";
    },
    updateActiveLayer: function(layerId) {
      if (this.castLocalFeaturesLocked) return;
      const playerStore = usePlayerStore();
      playerStore.setActiveLayer(layerId);
    },
    waveformToggleClick: function() {
      if (this.castLocalFeaturesLocked) return;
      this.waveformToggle = !this.waveformToggle;
      this.$emit("waveformToggle", this.waveformToggle)
    },
  },
  watch: {
    castLocalFeaturesLocked: function(locked) {
      if (locked) this.layersMenuOpen = false;
    },
    'castUi.connected': function(connected) {
      if (!connected) this.castSessionMenuOpen = false;
    },
    pod: function() {
      this.refreshLivepreviewImage();
    },
    fullscreen: function(newValue) {
      if (newValue) {
        // move the player controls to new location that is visible in fullscreen
        let defaultControls = document.getElementById("defaultControls");
        defaultControls.appendChild(this.$refs.customControls);
      } else {
        // move back the player controls to the previous location
        this.$refs.customControlsWrapper.appendChild(this.$refs.customControls);
      }
    },
    currentTime: function(newTime) {
      if (!this.draggingScrubSlider) {
        this.timeSliderValue = newTime * 100;
      }
    },
    timeSliderValue: function (value) {
      if (this.draggingScrubSlider) {
        this.seekPlayback(value / 100);
      }
    },
  },
  computed: {
    castDescriptionId: function() {
      return this.castUi ? (this.castUi.descriptionId || this.defaultCastDescriptionId) : undefined;
    },
    castLocalFeaturesLocked: function() {
      return !!(this.castUi?.busy || this.castUi?.active);
    },
    castPauseDisabled: function() {
      return !!this.castUi?.busy || (!!this.castUi?.active && !this.castUi.canPause);
    },
    castSeekDisabled: function() {
      return !!this.castUi?.busy || (!!this.castUi?.active && !this.castUi.canSeek);
    },
    castVolumeDisabled: function() {
      return !!this.castUi?.busy || (!!this.castUi?.active && !this.castUi.canControlVolume);
    },
    plyr: function() {
      return this.$parent.plyr();
    },
    playerTogglePlay: function() {
      if (this.playbackCommands) return promisify(this.playbackCommands.togglePlay);
      return promisify(this.plyr.togglePlay.bind(this.plyr));
    },
    playerPlay: function() {
      if (this.playbackCommands) return promisify(this.playbackCommands.play);
      return promisify(this.plyr.play.bind(this.plyr));
    },
    playerPause: function() {
      if (this.playbackCommands) return promisify(this.playbackCommands.pause);
      return promisify(this.plyr.pause.bind(this.plyr));
    },
    assetStates: function() {
      const commonStore = useCommonStore();
      return commonStore.assetStates;
    },
    customControlsStyle: function() {
      // let backgroundColor = this.$vuetify.theme.name === 'dark' ? "#232b36" : '#FFFFFF';
      // if (this.fullscreen) {
      //   backgroundColor = this.$vuetify.theme.name === 'dark' ? "#232b36" : "#FFFFFF";
      // }

      return {
        width: "100%",
        // backgroundColor: backgroundColor,
      };
    },
    isPlaying: function() {
      const playerStore = usePlayerStore();
      return playerStore.isPlaying;
    },
    duration: function() {
      const playerStore = usePlayerStore();
      return playerStore.duration;
    },
    currentTime: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    currentTimeAriaText: function() {
      const displayValue = this.playerTimeFormat
        ? this.toTimeMilliseconds(this.currentTime)
        : this.toTimeFrames(this.currentTime, this.transcodedAsset.framerate);

      return `${this.$t('common.timeTitle')}: ${displayValue}`;
    },
    timeFormatButtonLabel: function() {
      const formatLabel = this.playerTimeFormat
        ? this.$t('routes.pod.formatAsMilliseconds')
        : this.$t('routes.pod.formatAsFrames');

      return `${this.currentTimeAriaText}. ${formatLabel}`;
    },
    activeSubtitle: function() {
      const playerStore = usePlayerStore();
      return playerStore.getActiveSubtitle;
    },
    availableSubtitles: function() {
      const playerStore = usePlayerStore();
      return playerStore.availableSubtitles;
    },
    activeAudioDescription: function() {
      const playerStore = usePlayerStore();
      return playerStore.activeAudioDescription;
    },
    availableAudioDescriptions: function() {
      const playerStore = usePlayerStore();
      return playerStore.availableAudioDescriptions;
    },
    volume: function() {
      if (this.castUi?.active) return this.castUi.volume;
      const playerStore = usePlayerStore();
      return playerStore.volume;
    },
    muted: function() {
      if (this.castUi?.active) return this.castUi.muted;
      const playerStore = usePlayerStore();
      return playerStore.muted;
    },
    speed: function() {
      const playerStore = usePlayerStore();
      return playerStore.speed;
    },
    fullscreen: function() {
      const playerStore = usePlayerStore();
      return playerStore.fullscreen;
    },
    getActiveLayer: function() {
      const playerStore = usePlayerStore();
      return playerStore.getActiveLayer;
    },
    getAvailableLayers: function() {
      let availableLayers = [];

      const playerStore = usePlayerStore();
      const allLayers = playerStore.getAvailableLayers;

      if (allLayers.COMMENTS) {
        availableLayers.push({id: allLayers.COMMENT, text: this.$t('routes.pod.commentsLayer')});
      }
      if (allLayers.AWSDETECTEDTEXT) {
        availableLayers.push({id: allLayers.AWSDETECTEDTEXT, text: this.$t('routes.pod.awsTextLayer')});
      }
      if (allLayers.AWSDETECTEDLABELS) {
        availableLayers.push({id: allLayers.AWSDETECTEDLABELS, text: this.$t('routes.pod.awsLabelsLayer')});
      }

      return availableLayers;
    },
    volumeIcon: function() {
      if (this.volume == 0 || this.muted) {
        return 'mdi-volume-off';
      }

      if (this.volume <= 0.3) {
        return 'mdi-volume-low';
      }

      if (this.volume <= 0.65) {
        return 'mdi-volume-medium';
      }

      return "mdi-volume-high";
    },
    isActiveSubtitle: function() {
      return (subtitle) => {
        if (this.castUi?.active) {
          if (this.castUi.hasUnsupportedCaption) return false;
          return this.castUi.captionAssetId == null
            ? !subtitle.url
            : !!subtitle.url && subtitle.id === this.castUi.captionAssetId;
        }
        if (!this.activeSubtitle) {
          return !subtitle.url;
        }

        if (this.activeSubtitle.isTranscript) {
          return this.activeSubtitle.transcriptId === subtitle.transcriptId;
        }

        return this.activeSubtitle.id === subtitle.id;
      }
    },
    isActiveAudioDescription: function() {
      return (audioDescription) => {
        if (!this.activeAudioDescription) {
          return !audioDescription.url;
        }

        return this.activeAudioDescription.id === audioDescription.id;
      }
    }
  }
};
</script>

<style>
.previewWindow {
  display: none;
  position: fixed;
  width: 140px;
  height: 80px;
  border: 1px solid #FFFFFF;
  z-index: 1200;
  background-size: 19600px 80px;
}

.player-controls-second-row {
  padding: 10px;
  border-radius: 10px;
  flex-direction: row;
  background-color: rgb(var(--v-theme-tw-surface-accent));
  color: rgb(var(--v-theme-on-surface-variant));
}

.player-controls-time-format-btn {
  min-width: auto;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 6px !important;
  margin: 0 !important;
  display: inline-flex;
  align-items: baseline;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  letter-spacing: inherit !important;
  text-transform: none !important;
  line-height: inherit;
  vertical-align: baseline;
  align-self: baseline;
}

.player-controls-second-row > div:nth-child(1) {
  width: 40%;
  min-width: 160px;
}
.player-controls-second-row > div:nth-child(2) {
  width: 20%;
  min-width: 120px;
}
.player-controls-second-row > div:nth-child(3) {
  width: 40%;
  min-width: 160px;
  justify-content: right;
}

@media only screen and (max-width: 600px) {
  .player-controls-second-row {
    flex-direction: column !important;
  }

  .player-controls-second-row > div {
    width: 100% !important;
    justify-content: center !important;
  }
}

.v-theme--light .player-controls-second-row{
  background-color: rgb(var(--v-theme-podSidemenuButtons));
  color: rgb(var(--v-theme-on-surface));

  .playback-time {
    color: rgb(var(--v-theme-on-surface));
  }
}

.v-theme--dark .player-controls-second-row{
  background-color: rgb(var(--v-theme-tw-surface-accent));

  .playback-time {
    color: rgb(var(--v-theme-on-surface-variant));
  }
}
</style>
